import { computed, makeObservable, observable } from 'mobx';

import type { Aggregate } from 'edit/Aggregate';
import type { SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { formatSchedulingWindowPath, parseSchedulingWindowPath } from 'edit/reference/schedulingWindowPath';
import type { SchedulingWindowSchedule } from 'api/scheduling_windows/endpoints/types';

import type { EditableSchedulingWindow } from './EditableSchedulingWindow';
import { dayOfWeekName, monthName, nthOfMonthName, periodName, weekOfMonthName } from './scheduleLabels';

// Monotonic source of stable per-instance keys for React list rendering. A new
// rule has no server id, and array index is unstable across add / remove /
// reorder, so each instance gets a key at construction that never changes.
let nextScheduleKey = 0;

/**
 * A date bound as the form holds it: the day, whether a time override is switched
 * on, and the time itself.
 *
 * The wire carries all of it as ONE `timestamp without time zone`, and the
 * previous form recovered the pieces with string surgery on every render
 * (`getDatePart` / `getTimePart` / `combineDatetime`). Splitting it in the model
 * instead gives each piece a field a control can bind to directly.
 *
 * `hasTime` is genuinely independent of `time`, not a restatement of
 * `time !== null`: `splitDateBound` never returns one set without the other, but
 * the form reaches `hasTime: false` with a time still remembered (the operator
 * switched the override off) and `hasTime: true` with none yet (they just switched
 * it on). Which is why `joinDateBound` has to be told.
 */
interface DateBound {
  /** The day; `YYYY-MM-DD`. */
  readonly date: string | null;
  readonly hasTime: boolean;
  readonly time: string | null;
}

const splitDateBound = (wire: string | null): DateBound => {
  if (wire === null || wire === '') return { date: null, hasTime: false, time: null };
  const [date, rest] = wire.split('T');
  if (rest === undefined) return { date, hasTime: false, time: null };
  const time = rest.substring(0, 8);
  // Midnight is how "no override" is stored: a timestamp column cannot hold a
  // date without a time, so the backend renders the bare day as T00:00:00. Any
  // operator who genuinely wants midnight gets the same result either way.
  const hasTime = !time.startsWith('00:00');
  return { date, hasTime, time: hasTime ? time : null };
};

const joinDateBound = (date: string | null, hasTime: boolean, time: string | null): string | null => {
  if (date === null || date === '') return null;
  return hasTime && time !== null && time !== '' ? `${date}T${time}` : date;
};

/** `HH:MM` — a rule's times are stored to the second, but a summary reads better without. */
const shortTime = (time: string): string => time.substring(0, 5);

/**
 * Phrases a from/until pair, tolerating either half being absent — the backend
 * requires both for most of them, but an operator mid-edit has only one. A
 * one-sided range reads as the bare value, which is the honest rendering of a
 * half-finished edit.
 */
const rangePhrase = (from: string | null, until: string | null): string | null => {
  if (from !== null && until !== null) return from === until ? from : `${from} to ${until}`;
  return from ?? until;
};

/** Names a numeric field, or `null` when it is not set. */
const named = (value: number | null, name: (n: number) => string): string | null => (value === null ? null : name(value));

/**
 * The opening hours. One-sided is spelled out rather than left as a bare time —
 * "Open 09:00" does not say whether that is when it opens or closes.
 */
const timesPhrase = (start: string | null, end: string | null): string | null => {
  if (start !== null && end !== null) return `${shortTime(start)}–${shortTime(end)}`;
  if (start !== null) return `from ${shortTime(start)}`;
  if (end !== null) return `until ${shortTime(end)}`;
  return null;
};

/**
 * One opening rule of a scheduling window, as a reactive child of an
 * `EditableSchedulingWindow`.
 *
 * The parent→child relationship is owned by a shared `Aggregate` that both sides
 * hold: this child routes its back-reference and self-delete through it, so the
 * parent never exposes array mutation. Every user-fillable field is a public
 * observable, assigned directly from the form.
 *
 * There is deliberately **no cross-field validation here**. Every rule the old
 * form checked client-side — start ≠ end, both-or-neither date bounds, month
 * 1–12, the nth-of-month ordering — is enforced by the route, which answers 400
 * with the message the dialog shows. See the roadmap in the repo README for what
 * has to exist before client-side validation returns.
 */
export class EditableSchedule {
  private readonly _aggregate: Aggregate<EditableSchedulingWindow, EditableSchedule>;
  closedDuringWindow: SchedulingWindowReference | null = null;
  /** The day the rule starts applying; `YYYY-MM-DD`. */
  dateFrom: string | null = null;
  /**
   * Whether `dateFrom` carries a time override at all — the checkbox in the form,
   * held as its own field rather than derived from `dateFromTime !== null`.
   *
   * Deriving it would make the time input unmountable while being typed into: an
   * empty time control reports `''` (and a *partially* entered native time input
   * reports `''` too), which clears `dateFromTime`, which would untick the
   * checkbox and take the input away mid-keystroke. Same trap as binding a
   * reference picker's value to a never-null derived getter.
   */
  dateFromHasTime = false;
  /** Optional time-of-day overriding the rule's start time on `dateFrom` only. */
  dateFromTime: string | null = null;
  dateUntil: string | null = null;
  /** Whether `dateUntil` carries a time override; see {@link dateFromHasTime}. */
  dateUntilHasTime = false;
  /** Optional time-of-day overriding the rule's end time on `dateUntil` only. */
  dateUntilTime: string | null = null;
  dayOfWeekFrom: number | null = null;
  dayOfWeekUntil: number | null = null;
  description: string | null = null;
  endTime: string | null = null;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `schedule-${(nextScheduleKey += 1)}`;
  monthFrom: number | null = null;
  monthUntil: number | null = null;
  name: string | null = null;
  nthOfMonthFrom: number | null = null;
  nthOfMonthFromInWindow: SchedulingWindowReference | null = null;
  nthOfMonthUntil: number | null = null;
  nthOfMonthUntilInWindow: SchedulingWindowReference | null = null;
  openDuringWindow: SchedulingWindowReference | null = null;
  shiftIfClosedWindow: SchedulingWindowReference | null = null;
  shiftOpenDays: number | null = null;
  /**
   * Whether the advanced fields (nth-of-month and the cross-window settings) are on
   * screen. Purely presentational — it is not part of `toSave`, so it cannot make a
   * rule dirty. Starts open when the loaded rule already uses any of them, so an
   * existing setting is never hidden from the operator who has to maintain it.
   */
  showAdvanced = false;
  startTime: string | null = null;
  /**
   * Timezone override, or `null` for "use the window's timezone".
   *
   * `null` is the *stored* meaning of inherit — the column is nullable and the
   * evaluator resolves it per evaluation — and it is what `toSave` emits, so
   * inherit stays a live link to the window rather than a snapshot of whatever
   * the window's zone was when the rule was last saved.
   */
  timezone: string | null = null;
  /**
   * When true, month / week-of-month / nth-of-month are counted in the global
   * fiscal calendar. Requires a saved Platform fiscal periods config.
   */
  useFiscalPeriods = false;
  weekOfMonthFrom: number | null = null;
  weekOfMonthUntil: number | null = null;

  /**
   * @param aggregate the shared parent→children owner; the back-reference and
   *   self-delete route through it.
   * @param wire when present, seeds the fields from a loaded rule; when absent,
   *   the field defaults stand (a fresh, empty rule that opens the window
   *   whenever nothing narrows it).
   */
  constructor(aggregate: Aggregate<EditableSchedulingWindow, EditableSchedule>, wire?: SchedulingWindowSchedule) {
    this._aggregate = aggregate;
    makeObservable(this, {
      closedDuringWindow: observable.ref,
      dateFrom: observable,
      dateFromHasTime: observable,
      dateFromTime: observable,
      dateUntil: observable,
      dateUntilHasTime: observable,
      dateUntilTime: observable,
      dayOfWeekFrom: observable,
      dayOfWeekUntil: observable,
      description: observable,
      endTime: observable,
      monthFrom: observable,
      monthUntil: observable,
      name: observable,
      nthOfMonthFrom: observable,
      nthOfMonthFromInWindow: observable.ref,
      nthOfMonthUntil: observable,
      nthOfMonthUntilInWindow: observable.ref,
      openDuringWindow: observable.ref,
      shiftIfClosedWindow: observable.ref,
      shiftOpenDays: observable,
      showAdvanced: observable,
      startTime: observable,
      summary: computed,
      timezone: observable,
      useFiscalPeriods: observable,
      weekOfMonthFrom: observable,
      weekOfMonthUntil: observable,
    });

    if (wire !== undefined) {
      const from = splitDateBound(wire.date_from);
      const until = splitDateBound(wire.date_until);
      this.closedDuringWindow = parseSchedulingWindowPath(wire.closed_during_scheduling_window_path);
      this.dateFrom = from.date;
      this.dateFromHasTime = from.hasTime;
      this.dateFromTime = from.time;
      this.dateUntil = until.date;
      this.dateUntilHasTime = until.hasTime;
      this.dateUntilTime = until.time;
      this.dayOfWeekFrom = wire.day_of_week_from;
      this.dayOfWeekUntil = wire.day_of_week_until;
      this.description = wire.description;
      this.endTime = wire.end_time;
      this.monthFrom = wire.month_from;
      this.monthUntil = wire.month_until;
      this.name = wire.name;
      this.nthOfMonthFrom = wire.nth_of_month_from;
      this.nthOfMonthFromInWindow = parseSchedulingWindowPath(wire.nth_of_month_from_in_scheduling_window_path);
      this.nthOfMonthUntil = wire.nth_of_month_until;
      this.nthOfMonthUntilInWindow = parseSchedulingWindowPath(wire.nth_of_month_until_in_scheduling_window_path);
      this.openDuringWindow = parseSchedulingWindowPath(wire.open_during_scheduling_window_path);
      this.shiftIfClosedWindow = parseSchedulingWindowPath(wire.shift_if_closed_window_path);
      this.shiftOpenDays = wire.shift_open_days;
      this.startTime = wire.start_time;
      this.timezone = wire.timezone;
      this.useFiscalPeriods = wire.use_fiscal_periods;
      this.weekOfMonthFrom = wire.week_of_month_from;
      this.weekOfMonthUntil = wire.week_of_month_until;
      this.showAdvanced = this.usesAdvancedSettings;
    }
  }

  /** Back-reference to the owning window, via the shared aggregate. */
  get schedulingWindow(): EditableSchedulingWindow {
    return this._aggregate.parent;
  }

  /**
   * One line describing what this rule does, for the collapsed card header.
   *
   * A name or description the operator wrote wins — they said it better than we can.
   * Otherwise the set fields are phrased into a sentence, so a collapsed rule is
   * still identifiable without expanding it. Presentational, and `computed` because
   * every card header reads it on every keystroke in any of its fields.
   */
  get summary(): string {
    if (this.name !== null && this.name !== '') return this.name;
    if (this.description !== null && this.description !== '') return this.description;

    const unit = this.useFiscalPeriods ? 'period' : 'month';
    const days = rangePhrase(named(this.dayOfWeekFrom, dayOfWeekName), named(this.dayOfWeekUntil, dayOfWeekName));
    const weeks = rangePhrase(named(this.weekOfMonthFrom, weekOfMonthName), named(this.weekOfMonthUntil, weekOfMonthName));
    const monthsOrPeriods = rangePhrase(
      named(this.monthFrom, this.useFiscalPeriods ? periodName : monthName),
      named(this.monthUntil, this.useFiscalPeriods ? periodName : monthName)
    );
    const nth = rangePhrase(named(this.nthOfMonthFrom, nthOfMonthName), named(this.nthOfMonthUntil, nthOfMonthName));
    const dates = rangePhrase(this.dateFrom, this.dateUntil);

    const parts = [
      timesPhrase(this.startTime, this.endTime),
      days,
      weeks === null ? null : `in the ${weeks} week of the ${unit}`,
      nth === null ? null : `on the ${nth} day of the ${unit}`,
      monthsOrPeriods,
      dates,
    ].filter((part): part is string => part !== null);

    const head = parts.length === 0 ? 'Always open' : `Open ${parts.join(', ')}`;
    return this.useFiscalPeriods ? `${head} (fiscal periods)` : head;
  }

  /**
   * Whether the rule uses any of the fields behind the "advanced" toggle. Read once
   * at construction to decide whether that section starts open.
   */
  get usesAdvancedSettings(): boolean {
    return (
      this.nthOfMonthFrom !== null ||
      this.nthOfMonthUntil !== null ||
      this.nthOfMonthFromInWindow !== null ||
      this.nthOfMonthUntilInWindow !== null ||
      this.shiftIfClosedWindow !== null ||
      this.shiftOpenDays !== null ||
      this.openDuringWindow !== null ||
      this.closedDuringWindow !== null
    );
  }

  /** Remove this rule from its window. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape.
   *
   * Every key is emitted, never omitted: the type declares them
   * required-presence so forgetting one is a compile error rather than a field
   * that silently stops being saved, and the backend normalizes an explicit
   * `null` for all of them.
   */
  toSave(): SchedulingWindowSchedule {
    return {
      name: this.name,
      description: this.description,
      start_time: this.startTime,
      end_time: this.endTime,
      date_from: joinDateBound(this.dateFrom, this.dateFromHasTime, this.dateFromTime),
      date_until: joinDateBound(this.dateUntil, this.dateUntilHasTime, this.dateUntilTime),
      month_from: this.monthFrom,
      month_until: this.monthUntil,
      week_of_month_from: this.weekOfMonthFrom,
      week_of_month_until: this.weekOfMonthUntil,
      day_of_week_from: this.dayOfWeekFrom,
      day_of_week_until: this.dayOfWeekUntil,
      nth_of_month_from: this.nthOfMonthFrom,
      nth_of_month_until: this.nthOfMonthUntil,
      // Inherit is sent as `null`, NOT as the window's current timezone. Baking
      // it (what the old form did) turned inherit into a snapshot: a later change
      // to the window's timezone left the rule evaluating in the stale one while
      // the window header showed the new one. `null` keeps it a live link, which
      // the evaluator resolves per evaluation against the rule's own window.
      timezone: this.timezone,
      shift_open_days: this.shiftOpenDays,
      shift_if_closed_window_path: formatSchedulingWindowPath(this.shiftIfClosedWindow),
      nth_of_month_from_in_scheduling_window_path: formatSchedulingWindowPath(this.nthOfMonthFromInWindow),
      nth_of_month_until_in_scheduling_window_path: formatSchedulingWindowPath(this.nthOfMonthUntilInWindow),
      open_during_scheduling_window_path: formatSchedulingWindowPath(this.openDuringWindow),
      closed_during_scheduling_window_path: formatSchedulingWindowPath(this.closedDuringWindow),
      use_fiscal_periods: this.useFiscalPeriods,
    };
  }
}
